// Utilities
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import * as React from "react";
import { DndProvider, useDragDropManager } from "react-dnd";
import { MultiBackend, createTransition } from "react-dnd-multi-backend/dist/esm";
import { TouchBackend } from "react-dnd-touch-backend";
import { isRefable } from "react-dnd/dist/esm/decorators/utils";
let customBackends;
/**
 * Options for the Touch backend when it is used as the sole backend in hosts where the native
 * HTML5 drag-and-drop API is unavailable. `enableMouseEvents` lets it synthesize drag operations
 * from mouse input (in addition to touch) without depending on native HTML5 DnD events.
 */ const pointerBackendOptions = {
    enableMouseEvents: true
};
/**
 * Own property set on the backend instance created for the direct Touch/pointer backend case (see
 * `pointerBackendFactory` below). Consumers (e.g. DnDPreviewUtilities) use `isPointerDndBackend` to
 * detect this backend reliably. This is deliberately NOT based on `backend.constructor.name`: production
 * bundles minify/mangle class names (see webpack.main.config.js terser options), so a name-based check
 * silently breaks in production even though it can pass in local/dev builds.
 */ const pointerDndBackendMarker = "__isPointerDndBackend";
/**
 * Wraps `TouchBackend` so the created backend instance can be reliably identified later (see
 * `isPointerDndBackend`) without relying on its constructor name, which minifiers rename in production.
 */ const pointerBackendFactory = (manager, context, options)=>{
    const backend = TouchBackend(manager, context, options);
    backend[pointerDndBackendMarker] = true;
    return backend;
};
/**
 * Returns true if `backend` is the direct Touch/pointer backend created via `pointerBackendFactory`
 * (used when the `usePointerBackendInDnd` setting is on). Safe to call with any value.
 */ export function isPointerDndBackend(backend) {
    return typeof backend === "object" && backend != null && backend[pointerDndBackendMarker] === true;
}
/**
 * Suppresses native text/element selection while a drag is in progress under the Touch (pointer)
 * backend. The Touch backend synthesizes drags from real mouse events, so - unlike the HTML5 drag
 * backend - the browser still performs its default text selection as the pointer moves. That causes
 * unrelated cards/rows (e.g. in premium plan board and grid views) to become selected mid-drag.
 *
 * This guard subscribes to the react-dnd monitor and, while dragging, sets `user-select: none` on
 * the document body and clears any in-progress selection. It renders nothing and only mounts under
 * the pointer backend, so the default selection behavior is untouched everywhere else.
 */ const PointerDragSelectionGuard = ()=>{
    const dragDropManager = useDragDropManager();
    React.useEffect(()=>{
        const monitor = dragDropManager.getMonitor();
        const body = document.body;
        const bodyStyle = body.style;
        let isGuardApplied = false;
        // Remember any pre-existing inline user-select values so we restore exactly what was
        // there when the drag ends, rather than clobbering styling owned by other code.
        let previousUserSelect = "";
        let previousWebkitUserSelect = "";
        const setSelectionSuppressed = (suppressed)=>{
            if (suppressed === isGuardApplied) {
                return;
            }
            isGuardApplied = suppressed;
            if (suppressed) {
                previousUserSelect = bodyStyle.userSelect;
                previousWebkitUserSelect = bodyStyle.webkitUserSelect ?? "";
                bodyStyle.userSelect = "none";
                bodyStyle.webkitUserSelect = "none";
                // Clear any selection that began on mousedown before the drag threshold was crossed.
                window.getSelection?.()?.removeAllRanges();
            } else {
                bodyStyle.userSelect = previousUserSelect;
                bodyStyle.webkitUserSelect = previousWebkitUserSelect;
            }
        };
        const unsubscribe = monitor.subscribeToStateChange(()=>{
            setSelectionSuppressed(monitor.isDragging());
        });
        return ()=>{
            unsubscribe();
            setSelectionSuppressed(false);
        };
    }, [
        dragDropManager
    ]);
    return null;
};
/**
 * Returns custom dnd backend that updates backend type.
 */ function getCustomBackendOptions(setDndBackend) {
    if (!customBackends) {
        customBackends = HTML5toTouch;
        // Replace the transitions with ones that will add/remove the touch preview element
        // Transitions are used in react-dnd-multi-backend to determine whether the dnd backend
        // should be switched to another backend based on the event.
        // We add logic to the transition callback to update the AppContext to the current dnd backend.
        customBackends.backends[0].transition = createTransition("mousedown", (event)=>{
            if (!!event.type && -1 === event.type.indexOf("touch") && -1 !== event.type.indexOf("mouse")) {
                setDndBackend("Html5");
                return true;
            }
            return false;
        });
        customBackends.backends[1].transition = createTransition("touchstart", (event)=>{
            if (event.touches != null) {
                setDndBackend("Touch");
                return true;
            }
            return false;
        });
    }
    return customBackends;
}
/**
 * Wraps a component with Drag and Drop context provider. Returns class component.
 * @param Component to be wrapped with DnD context
 * @param setDndBackend Callback to be called when the dnd backend changes
 * @param usePointerBackend When true, uses the Touch backend (with mouse events enabled) instead of
 * the HTML5/Touch multi-backend. Some embedded webviews (e.g. Outlook Classic / Win32) do not fire
 * native HTML5 drag-and-drop events, which the HTML5 backend relies on, so drag-and-drop silently
 * fails there. The Touch backend synthesizes drag from pointer events and does not depend on the
 * native HTML5 DnD API, restoring drag-and-drop in those hosts.
 */ export function withDndContext(Component, setDndBackend, usePointerBackend = false) {
    return class extends React.Component {
        componentDidMount() {
            // Keep child-view AppContexts aligned with the shell DnD backend when the direct
            // pointer (Touch) backend is used. This does not depend on the react-dnd context,
            // so it runs here instead of via a rendered child component. `backend` is fixed
            // ("Touch") and `setDndBackend` is stable, so this only needs to fire once on mount.
            if (usePointerBackend) {
                void setDndBackend("Touch");
            }
        }
        render() {
            const child = /*#__PURE__*/ React.createElement(Component, {
                ...this.props,
                ref: isRefable(Component) ? this.ref : null
            });
            if (usePointerBackend) {
                return /*#__PURE__*/ React.createElement(DndProvider, {
                    backend: pointerBackendFactory,
                    options: pointerBackendOptions
                }, /*#__PURE__*/ React.createElement(PointerDragSelectionGuard, null), child);
            }
            return /*#__PURE__*/ React.createElement(DndProvider, {
                backend: MultiBackend,
                options: getCustomBackendOptions(setDndBackend)
            }, child);
        }
        constructor(props){
            super(props), this.ref = /*#__PURE__*/ React.createRef();
        }
    };
}
